import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { getDefaultAdminRoute, isSystemAdmin, resolveOrgRouteAccess } from '@/utils/routeAccess'

// Import components
const LoginPage = () => import('@/pages/LoginPage.vue')
const AdminLayout = () => import('@/components/layout/AdminLayout.vue')
const FacebookOAuthCallbackPage = () => import('@/pages/FacebookOAuthCallbackPage.vue')
const GoogleOAuthCallbackPage = () => import('@/pages/GoogleOAuthCallbackPage.vue')
const PrivacyPolicyPage = () => import('@/pages/PrivacyPolicyPage.vue')
const TermsOfServicePage = () => import('@/pages/TermsOfServicePage.vue')
const ContactPage = () => import('@/pages/ContactPage.vue')

// Admin pages
const DashboardPage = () => import('@/pages/DashboardPage.vue')
const FormRequestPage = () => import('@/pages/FormRequestPage.vue')
const AdAccountPage = () => import('@/pages/AdAccountPage.vue')
const ViaFacebookPage = () => import('@/pages/ViaFacebookPage.vue')
const BillingHistoryPage = () => import('@/pages/BillingHistoryPage.vue')
const CampaignPage = () => import('@/pages/CampaignPage.vue')
const UserManagementPage = () => import('@/pages/UserManagementPage.vue')
const RolePermissionPage = () => import('@/pages/RolePermissionPage.vue')
const PermissionManagementPage = () => import('@/pages/PermissionManagementPage.vue')
const RoleManagementPage = () => import('@/pages/RoleManagementPage.vue')
const PermissionGroupManagementPage = () => import('@/pages/PermissionGroupManagementPage.vue')
const PackageManagementPage = () => import('@/pages/PackageManagementPage.vue')
const MyPackagePage = () => import('@/pages/MyPackagePage.vue')
const BankBalancePage = () => import('@/pages/BankBalancePage.vue')
const TelegramCompanyPage = () => import('@/pages/TelegramCompanyPage.vue')
const TelegramGroupPage = () => import('@/pages/TelegramGroupPage.vue')
const TelegramProductivityReportPage = () => import('@/pages/TelegramProductivityReportPage.vue')
const TelegramStaffDetailReportPage = () => import('@/pages/TelegramStaffDetailReportPage.vue')
const CustomerMessagesByHourPage = () => import('@/pages/CustomerMessagesByHourPage.vue')
const RentalCustomersPage = () => import('@/pages/RentalCustomersPage.vue')
const BankInvoicePage = () => import('@/pages/BankInvoicePage.vue')
const PaymentInvoiceManagementPage = () => import('@/pages/PaymentInvoiceManagementPage.vue')
const PaymentCardPage = () => import('@/pages/PaymentCardPage.vue')
const CurrencyExchangePage = () => import('@/pages/CurrencyExchangePage.vue')
const AssetsPage = () => import('@/pages/AssetsPage.vue')
const FinancialDashboardPage = () => import('@/pages/FinancialDashboardPage.vue')
const TagSettingsPage = () => import('@/pages/TagSettingsPage.vue')
const ShieldPage = () => import('@/pages/ShieldPage.vue')
const BmAdminAlertPage = () => import('@/pages/BmAdminAlertPage.vue')
const NotificationSettingsPage = () => import('@/pages/NotificationSettingsPage.vue')
const CustomerSheetPage = () => import('@/pages/CustomerSheetPage.vue')
const RunCommandPage = () => import('@/pages/RunCommandPage.vue')
const ExtensionBillPage = () => import('@/pages/ExtensionBillPage.vue')
const LicenseKeyManagementPage = () => import('@/pages/LicenseKeyManagementPage.vue')
const ActionHistoryPage = () => import('@/pages/ActionHistoryPage.vue')
const SelectOrganizationPage = () => import('@/pages/SelectOrganizationPage.vue')
const AcceptInvitePage = () => import('@/pages/AcceptInvitePage.vue')


const routes = [
  {
    path: '/',
    redirect: '/login'
  },
  {
    path: '/login',
    name: 'Login',
    component: LoginPage,
    meta: { 
      requiresGuest: true,
      title: 'Đăng nhập'
    }
  },
  {
    path: '/facebook-oauth-callback',
    name: 'FacebookOAuthCallback',
    component: FacebookOAuthCallbackPage,
    meta: {
      title: 'Facebook OAuth',
      requiresAuth: false
    }
  },
  {
    path: '/google-oauth-callback',
    name: 'GoogleOAuthCallback',
    component: GoogleOAuthCallbackPage,
    meta: {
      title: 'Google OAuth',
      requiresAuth: false
    }
  },
  {
    path: '/privacy-policy',
    name: 'PrivacyPolicy',
    component: PrivacyPolicyPage,
    meta: { 
      requiresGuest: false,
      requiresAuth: false,
      title: 'Chính sách quyền riêng tư'
    }
  },
  {
    path: '/terms-of-service',
    name: 'TermsOfService',
    component: TermsOfServicePage,
    meta: { 
      requiresAuth: false,
      title: 'Điều khoản dịch vụ'
    }
  },
  {
    path: '/contact',
    name: 'Contact',
    component: ContactPage,
    meta: { 
      requiresAuth: false,
      title: 'Thông tin liên hệ'
    }
  },
  {
    path: '/:orgId/admin',
    component: AdminLayout,
    meta: {
      requiresAuth: true
    },
    children: [
      {
        path: '',
        redirect: { name: 'Dashboard' }
      },
      {
        path: 'dashboard',
        name: 'Dashboard',
        component: DashboardPage,
        meta: { 
          title: 'Tổng quan'
        }
      },
      {
        path: 'form-request',
        name: 'FormRequest',
        component: FormRequestPage,
        meta: { 
          title: 'Form request'
        }
      },
      {
        path: 'assets',
        name: 'Assets',
        component: AssetsPage,
        meta: { 
          title: 'Quản lý tài sản',
          permission: ['view-via-full', 'view-via-own', 'view-facebook-via']
        }
      },
      {
        path: 'ad-accounts',
        name: 'AdAccounts',
        component: AdAccountPage,
        meta: { 
          title: 'Tài khoản quảng cáo',
          permission: 'view-ad-accounts'
        }
      },
      {
        path: 'via-facebook',
        name: 'ViaFacebook',
        component: ViaFacebookPage,
        meta: { 
          title: 'Via Facebook',
          permission: ['view-via-full', 'view-via-own', 'view-facebook-via']
        }
      },
      {
        path: 'billing-history',
        name: 'BillingHistory',
        component: BillingHistoryPage,
        meta: { 
          title: 'Lịch sử thanh toán',
          permission: 'view-billing'
        }
      },
      {
        path: 'campaigns',
        name: 'Campaigns',
        component: CampaignPage,
        meta: { 
          title: 'Chiến dịch quảng cáo',
          permission: 'view-campaigns'
        }
      },
      {
        path: 'users',
        name: 'UserManagement',
        component: UserManagementPage,
        meta: { 
          title: 'Quản lý người dùng',
          permission: 'manage-users'
        }
      },
      {
        path: 'roles',
        name: 'RolePermission',
        component: RolePermissionPage,
        meta: {
          title: 'Phân quyền',
          permission: 'manage-permissions'
        }
      },
      {
        path: 'package-management',
        name: 'PackageManagement',
        component: PackageManagementPage,
        meta: {
          title: 'Quản lý gói',
          requireSysadmin: true
        }
      },
      {
        path: 'my-package',
        name: 'MyPackage',
        component: MyPackagePage,
        meta: {
          title: 'Gói của bạn'
        }
      },
      {
        path: 'permissions',
        name: 'PermissionManagement',
        component: PermissionManagementPage,
        meta: { 
          title: 'Quản lý quyền',
          permission: 'manage-permissions'
        }
      },
      {
        path: 'role-management',
        name: 'RoleManagement',
        component: RoleManagementPage,
        meta: { 
          title: 'Quản lý vai trò',
          permission: 'manage-users'
        }
      },
      {
        path: 'permission-groups',
        name: 'PermissionGroupManagement',
        component: PermissionGroupManagementPage,
        meta: { 
          title: 'Quản lý nhóm quyền',
          permission: 'manage-permissions'
        }
      },
      {
        path: 'bank-balance',
        name: 'BankBalance',
        component: BankBalancePage,
        meta: { 
          title: 'Số dư ngân hàng',
          permission: 'view-bank-balances'
        }
      },
      {
        path: 'telegram-company',
        name: 'TelegramCompany',
        component: TelegramCompanyPage,
        meta: { 
          title: 'Telegram công ty',
          permission: 'view-telegram-companies'
        }
      },
      {
        path: 'telegram-groups/:companyId',
        name: 'TelegramGroup',
        component: TelegramGroupPage,
        meta: { 
          title: 'Group Telegram',
          permission: 'view-telegram-companies'
        }
      },
      {
        path: 'telegram-productivity-report',
        name: 'TelegramProductivityReport',
        component: TelegramProductivityReportPage,
        meta: { 
          title: 'Báo cáo năng suất Telegram',
          permission: 'view-telegram-companies'
        }
      },
      {
        path: 'telegram-staff-detail',
        name: 'TelegramStaffDetail',
        component: TelegramStaffDetailReportPage,
        meta: { 
          title: 'Chi tiết năng suất nhân viên',
          permission: 'view-telegram-companies'
        }
      },
      {
        path: 'customer-messages-by-hour',
        name: 'CustomerMessagesByHour',
        component: CustomerMessagesByHourPage,
        meta: { 
          title: 'Tần suất chat khách hàng',
          permission: 'view-telegram-companies'
        }
      },
      {
        path: 'rental-customers',
        name: 'RentalCustomers',
        component: RentalCustomersPage,
        meta: { 
          title: 'Khách hàng thuê TKQC',
          permission: 'view-rental-customers'
        }
      },
      {
        path: 'bank-invoices',
        name: 'BankInvoice',
        component: BankInvoicePage,
        meta: { 
          title: 'Hóa đơn ngân hàng',
          permission: 'view-bank-invoices'
        }
      },
      {
        path: 'payment-invoices',
        name: 'PaymentInvoiceManagement',
        component: PaymentInvoiceManagementPage,
        meta: { 
          title: 'Quản lý hóa đơn thanh toán',
          permission: 'view-billing'
        }
      },
      {
        path: 'payment-cards',
        name: 'PaymentCards',
        component: PaymentCardPage,
        meta: {
          title: 'Quản lý thẻ thanh toán',
          permission: 'card-group.view'
        }
      },
      {
        path: 'financial-dashboard',
        name: 'FinancialDashboard',
        component: FinancialDashboardPage,
        meta: {
          title: 'Tài chính & Thanh toán',
          permission: 'view-financial-dashboard'
        }
      },
      {
        path: 'currency-exchange',
        name: 'CurrencyExchange',
        component: CurrencyExchangePage,
        meta: { 
          title: 'Quy đổi tiền tệ'
        }
      },
      {
        path: 'tag-settings',
        name: 'TagSettings',
        component: TagSettingsPage,
        meta: {
          title: 'Cài đặt tag'
        }
      },
      {
        path: 'shields',
        name: 'Shield',
        component: ShieldPage,
        meta: {
          title: 'Tường lửa bảo vệ (Shield)'
        }
      },
      {
        path: 'action-history',
        name: 'ActionHistory',
        component: ActionHistoryPage,
        meta: {
          title: 'Lịch sử hành động',
          permission: 'view-action-history'
        }
      },
      {
        path: 'bm-admin-alert',
        name: 'BmAdminAlert',
        component: BmAdminAlertPage,
        meta: {
          title: 'Cảnh báo Admin BM',
          permission: 'manage-bm-admin-alerts'
        }
      },
      {
        path: 'notification-settings',
        name: 'NotificationSettings',
        component: NotificationSettingsPage,
        meta: {
          title: 'Cấu hình thông báo',
          permission: 'manage-notification-settings'
        }
      },
      {
        path: 'notification-settings/teams/:teamId',
        name: 'TeamNotificationSettings',
        component: NotificationSettingsPage,
        props: true,
        meta: {
          title: 'Thông báo theo team',
          permission: 'manage-notification-settings'
        }
      },
      {
        path: 'excel-sheets',
        name: 'CustomerSheet',
        component: CustomerSheetPage,
        meta: {
          title: 'Quản lý sheet khách hàng'
        }
      },
      {
        path: 'run-command',
        name: 'RunCommand',
        component: RunCommandPage,
        meta: {
          title: 'Chạy command',
          permission: 'run-admin-commands'
        }
      },
      {
        path: 'extension-bill',
        name: 'ExtensionBill',
        component: ExtensionBillPage,
        meta: {
          title: 'Extension tải bill'
        }
      },
      {
        path: 'license-keys',
        name: 'LicenseKeys',
        component: LicenseKeyManagementPage,
        meta: {
          title: 'Quản lý Key phần mềm',
          permission: 'manage-license-keys'
        }
      }
    ]
  },
  {
    path: '/select-organization',
    name: 'SelectOrganization',
    component: SelectOrganizationPage,
    meta: {
      requiresAuth: true,
      title: 'Chọn workspace'
    }
  },
  {
    path: '/invite/:token',
    name: 'AcceptInvite',
    component: AcceptInvitePage,
    meta: {
      requiresAuth: true,
      title: 'Lời mời tham gia tổ chức'
    }
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: (to) => {
      const authStore = useAuthStore()
      return authStore.isLoggedIn ? '/select-organization' : '/login'
    }
  }
]

export { routes }

const router = createRouter({
  history: createWebHistory(),
  routes
})

// Navigation guards
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()

  // Set page title
  if (to.meta && to.meta.title) {
    document.title = `${to.meta.title} - ${import.meta.env.VITE_APP_TITLE || 'Ads Management'}`
  }

  // Check authentication requirement
  if (to.matched.some(record => record.meta.requiresAuth)) {
    if (!authStore.isLoggedIn) {
      next({
        path: '/login',
        query: { redirect: to.fullPath }
      })
      return
    }

    // Org-scoping: validate/sync the org in the URL before checking permissions
    if (to.params.orgId) {
      const decision = resolveOrgRouteAccess({
        orgId: to.params.orgId,
        user: authStore.user,
        currentOrganizationId: authStore.currentOrganizationId
      })

      if (decision.action === 'redirect') {
        next({ path: decision.path, query: { redirect: to.fullPath } })
        return
      }

      if (decision.action === 'select') {
        try {
          await authStore.selectOrganization(decision.orgId)
        } catch (error) {
          next({ path: '/select-organization', query: { redirect: to.fullPath } })
          return
        }
      }
    }

    const requiredPermission = to.matched
      .map((record) => record.meta.permission)
      .filter(Boolean)
      .pop()

    if (
      requiredPermission &&
      !isSystemAdmin(authStore.user) &&
      !authStore.hasPermission(requiredPermission)
    ) {
      const fallback = getDefaultAdminRoute(authStore, to.params.orgId)
      if (to.path === fallback) {
        next()
        return
      }
      next({ path: fallback })
      return
    }

    // Hard gate: route yêu cầu admin/sysadmin → chặn user thường truy cập URL trực tiếp
    if (to.matched.some(record => record.meta.requireAdmin)) {
      const roles = (authStore.user && authStore.user.roles) || []
      const isAdmin = roles.some(r => r.slug === 'admin' || r.slug === 'sysadmin')
      if (!isAdmin) {
        next({ path: getDefaultAdminRoute(authStore, to.params.orgId) })
        return
      }
    }

    // Hard gate: route yêu cầu sysadmin (Super Admin toàn hệ thống) — chặt hơn
    // requireAdmin, "admin" (admin theo từng tổ chức) KHÔNG được truy cập.
    if (to.matched.some(record => record.meta.requireSysadmin)) {
      const roles = (authStore.user && authStore.user.roles) || []
      const isSysadmin = roles.some(r => r.slug === 'sysadmin')
      if (!isSysadmin) {
        next({ path: getDefaultAdminRoute(authStore, to.params.orgId) })
        return
      }
    }

    next()
  } else {
    next()
  }
})

// Handle navigation errors
router.onError((error) => {
  console.error('Router error:', error)
})

export default router
